import {
  Factory,
  Model,
  belongsTo,
  type Collection,
  type ModelInstance,
} from 'miragejs'
import type { ModelDefinition } from 'miragejs/-types'
import { randWord, rand, randBoolean, randChanceBoolean } from '@ngneat/falso'

import type { SchemaAttributes } from '../types.js'
import type { MockServer, MirageServer } from './server.js'
import { pageable, filterable } from './utils.js'
import { toNumber } from '@/utils/number.js'

type TagTextData = SchemaAttributes<'TagText'>

export const model = (Model as ModelDefinition<TagTextData>).extend({
  tag: belongsTo('tag'),
})

export const factory = Factory.extend<TagTextData>({
  text() {
    return randWord({ maxCharCount: 10 })
  },
  department_id() {
    return -1
  },
  in_client() {
    return randBoolean()
  },
  in_patient() {
    return randBoolean()
  },
  in_custom_field() {
    return randChanceBoolean({ chanceTrue: 0.25 })
  },
  afterCreate(tagText, server) {
    const departments = server.schema.all('department') as Collection<
      ModelInstance<SchemaAttributes<'Department'>>
    >

    if (!tagText.department_id || tagText.department_id === -1) {
      const departmentId = rand(departments.models.map((d) => d.id))

      tagText.update({ department_id: departmentId })
    }

    // Set readonly in afterCreate to avoid conflicts with reserved keyword
    tagText.update({ readonly: randChanceBoolean({ chanceTrue: 0.1 }) })
  },
})

export function routes(server: MockServer) {
  server.resource('tagText', { path: '/tag_texts' })

  server.get('/tag_texts', function (this: MirageServer, schema, request) {
    const tags = schema.all('tagText')

    const filtered = filterable(tags, request, {
      search: (item, value) =>
        value ? item.text.toLowerCase().includes(value.toLowerCase()) : true,
      department_id: (item, value) =>
        toNumber(item.department_id) === toNumber(value),
      in_client: (item, value) => item.in_client === (value === 'true'),
      in_patient: (item, value) => item.in_patient === (value === 'true'),
      in_custom_field: (item, value) =>
        item.in_custom_field === (value === 'true'),
    })

    const { data, meta } = pageable(filtered, request)

    return {
      ...this.serialize(data),
      meta: {
        pagination: meta,
      },
    }
  })
}

export function seeds(server: MockServer) {
  server.createList('tagText', 50)
}
